import * as React from 'react';

import Modal from '../Modal';

import DocumentList from './DocumentList';
import DocumentPreview from './DocumentPreview';
import { pickDefaultDocument, type IViewerDocument, type IViewerMetadata } from './documentUtils';

interface IDocumentViewerProps {
  documents: ReadonlyArray<IViewerDocument>;
  metadata?: IViewerMetadata | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Modal viewer that lists and previews documents attached to a bill or bill submission.
 * Operators select a document from the left rail; the right pane renders a category-aware
 * preview (PDF, image with zoom, sandboxed email iframe, or unsupported-fallback download link).
 */
const DocumentViewer = ({ documents, metadata, isOpen, onClose }: IDocumentViewerProps) => {
  const [selectedDocumentId, setSelectedDocumentId] = React.useState<string | null>(null);

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        setSelectedDocumentId(null);
        onClose();
      }
    },
    [onClose]
  );

  const effectiveSelectedId = selectedDocumentId ?? pickDefaultDocument(documents)?.id ?? null;

  const selectedDocument = React.useMemo(
    () => documents.find((d) => d.id === effectiveSelectedId) ?? null,
    [documents, effectiveSelectedId]
  );

  const handleSelectDocument = React.useCallback((doc: IViewerDocument) => {
    setSelectedDocumentId(doc.id);
  }, []);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      size="xl"
      className="h-[70vh] w-full max-w-7xl items-stretch justify-start overflow-hidden"
    >
      <Modal.Header onClose={onClose}>
        <div className="text-primary text-2xl font-semibold tracking-tight">Documents</div>
      </Modal.Header>
      <Modal.Body className="min-h-0 flex-1 flex-row items-stretch p-0">
        <DocumentList
          documents={documents}
          metadata={metadata}
          selectedDocumentId={effectiveSelectedId}
          onSelectDocument={handleSelectDocument}
        />
        <div className="flex flex-1">
          <DocumentPreview document={selectedDocument} />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default DocumentViewer;
